import { routes } from "@constants/routes";
import { ViewportLoading } from "@cpj-ai/panel-npm-package";
import { lazy, ReactNode, Suspense } from "react";
import { createBrowserRouter, RouteObject } from "react-router-dom";

const ProtectedRoute = lazy(() => import("./ProtectedRoute"));

// Layouts
const MainLayout = lazy(() => import("@layouts/Main"));
const AuthLayout = lazy(() => import("@layouts/Auth"));

// Auth Pages
const Login = lazy(() => import("@pages/Auth/Login"));

const NotFoundPage = lazy(() => import("@pages/NotFound"));
const SupportPage = lazy(() => import("@pages/Support"));
const ErrorPage = lazy(() => import("@pages/Error"));

// Main Pages
const CompaniesPage = lazy(() => import("@pages/Companies"));
const CompanyDetailPage = lazy(() => import("@pages/Companies/companyDetail"));
const JobSeekerViewPage = lazy(() => import("@pages/TalentPool/View"));
const CandidatePage = lazy(() => import("@pages/Candidate"));
const ReferralPage = lazy(() => import("@pages/Referral"));
const JobPositionsPage = lazy(() => import("@pages/JobPositions"));
const JobPositionsViewPage = lazy(() => import("@pages/JobPositions/View"));
const FinancialPage = lazy(() => import("@pages/Financial"));
const DashboardPage = lazy(() => import("@pages/Dashboard"));
const TalentPoolPage = lazy(() => import("@pages/TalentPool"));
const JobSeekerAssessmentsPrintPage = lazy(() => import("@pages/TalentPool/AssessmentsPrint"));

type RouteConfig = {
  path: string;
  element: ReactNode;
  children?: RouteConfig[];
  isProtected?: boolean;
  errorElement?: ReactNode;
  hasErrorBoundary?: boolean;
};

const withSuspense = (Component: React.ComponentType) => (
  <Suspense fallback={<ViewportLoading />}>
    <Component />
  </Suspense>
);

const createRoutes = (routes: RouteConfig[]): RouteObject[] =>
  routes.map(({ path, element, children, isProtected, errorElement, hasErrorBoundary }) => ({
    path,
    element: withSuspense(() => (isProtected ? <ProtectedRoute>{element}</ProtectedRoute> : element)),
    children: children && createRoutes(children),
    errorElement:
      !import.meta.env.VITE_DEBUG_MODE && (hasErrorBoundary || errorElement)
        ? withSuspense(() => errorElement ?? <ErrorPage />)
        : undefined,
  }));

const authRoutes: RouteConfig[] = [{ path: "", element: <Login />, isProtected: false }];

const mainRoutes: RouteConfig[] = [
  { path: routes.dashboard.main, element: <DashboardPage />, isProtected: true },
  { path: routes.companies.main, element: <CompaniesPage />, isProtected: true },
  { path: routes.companies.detail, element: <CompanyDetailPage />, isProtected: true },
  { path: routes.referral.main, element: <ReferralPage />, isProtected: true },
  { path: routes.jobPositions.main, element: <JobPositionsPage />, isProtected: true },
  { path: routes.jobPositions.view, element: <JobPositionsViewPage />, isProtected: true },
  { path: routes.support.main, element: <SupportPage />, isProtected: true },
  { path: routes.financial.main, element: <FinancialPage />, isProtected: true },
  { path: routes.talentPool.main, element: <TalentPoolPage />, isProtected: true },
  { path: routes.talentPool.detail, element: <CandidatePage />, isProtected: true },
  { path: routes.talentPool.fullDetail, element: <JobSeekerViewPage />, isProtected: true },
  { path: routes.talentPool.assessmentsPrint, element: <JobSeekerAssessmentsPrintPage />, isProtected: true },
];

const rootRoutes: RouteConfig[] = [
  {
    path: routes.auth.main,
    element: <AuthLayout />,
    children: authRoutes,
    hasErrorBoundary: true,
  },
  {
    path: "/",
    element: <MainLayout />,
    children: mainRoutes,
    hasErrorBoundary: true,
  },
  {
    path: routes.notFound,
    element: <NotFoundPage />,
    hasErrorBoundary: true,
  },
];

const MainRoutes = createBrowserRouter(createRoutes(rootRoutes));

export default MainRoutes;
